import createTableStore from "stores/templates/TableStore";
import { types, flow, applySnapshot } from "mobx-state-tree";
import { PUBLISH_STATUSES, RESPONSE_TYPES } from "constants/models";
import api from "services/api";
import { MSURL } from "utils/ms";
import { getNextPublishStatusWithoutScheduled } from "utils/publish";
import { isEmptyObject } from "utils";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { ms } from "date-fns/locale";

enum PublishStatus {
  DRAFT = "Draft",
  PREVIEW = "Preview",
  PUBLISHED = "Published",
  SCHEDULE_FOR_REMOVE = "ScheduleForRemove",
}

export interface Trivia {
  id: number;
  title: string;
  code?: string;
  publishStatus: PublishStatus;
}

export type Question = {
  code: string;
  complement: string;
  id: number;
  isRequired: string;
  publishStatus: string;
  responseType: string;
  title: string;
};

export const QuestionModel = types.model({
  code: types.string,
  complement: types.string,
  id: types.number,
  isRequired: types.boolean,
  publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  responseType: types.maybeNull(
    types.enumeration(Object.values(RESPONSE_TYPES)),
  ),
  title: types.string,
});

const TriviaModel = types
  .model({
    id: types.number,
    title: types.string,
    code: types.maybe(types.string),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    formData() {
      return {
        ...self,
      };
    },
  }));

export const TriviaDetailModel = types
  .model({
    id: types.number,
    title: types.string,
    code: types.string,
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    questions: types.array(QuestionModel),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }));

const pathTrivia = "/trivia/api/v1";
const TriviaStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${pathTrivia}/trivia`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: TriviaModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(TriviaDetailModel),
    isTriviaLoading: types.optional(types.boolean, false),
    isTriviaManaging: types.optional(types.boolean, false),
    isAddQuestionLoading: types.optional(types.boolean, false),
    isPublishing: types.optional(types.boolean, false),
  })
  .actions(self => ({
    localTriviaUpdate(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchTriviaDetails: flow(function*(id?: number) {
      try {
        self.isTriviaLoading = true;

        const details = yield api.get(
          `${pathTrivia}/trivia/${id}`,
          undefined,
          undefined,
          MSURL,
        );
        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTriviaLoading = false;
      }
    }),

    createTrivia: flow(function*(payload: any) {
      try {
        self.isTriviaManaging = true;
        const { id } = yield api.post(
          `${pathTrivia}/trivia`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTriviaManaging = false;
      }
    }),

    editTrivia: flow(function*(payload: any) {
      self.isTriviaManaging = true;

      try {
        return yield api.put(
          `${pathTrivia}/trivia/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTriviaManaging = false;
      }
    }),

    changeTriviaStatus: flow(function*(id: number, status: string) {
      try {
        self.isPublishing = true;
        const nextStatus = getNextPublishStatusWithoutScheduled(status);

        if (nextStatus == null) return;

        const data = {
          status: nextStatus,
        };

        const { success } = yield api.post(
          `${pathTrivia}/trivia/${id}/status`,
          data,
          undefined,
          MSURL,
        );

        if (success) {
          self.localTriviaUpdate(id, { publishStatus: nextStatus });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isPublishing = false;
      }
    }),

    updateQuestionOrder: flow(function*(payload: any) {
      try {
        self.isTriviaLoading = true;
        return yield api.post(
          `${pathTrivia}/trivia/update-order-question`,
          payload.newOrder,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTriviaLoading = false;
      }
    }),

    deleteQuestion: flow(function*(payload: { questionId: number }) {
      try {
        self.isTriviaLoading = true;
        return yield api.delete(
          `${pathTrivia}/question/${payload.questionId}`,
          undefined,
          undefined,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTriviaLoading = false;
      }
    }),

    addQuestionToTrivia: flow(function*(payload: {
      title: string;
      code: string;
      complement: string;
      isRequired: boolean;
      triviaID: number;
      displayOrder: number;
    }) {
      self.isAddQuestionLoading = true;

      try {
        const { id } = yield api.post(
          /* `/trivia/${payload.id}/add-question`, */
          `${pathTrivia}/question/`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isAddQuestionLoading = false;
      }
    }),

    editTriviaQuestion: flow(function*(payload: {
      id: number;
      title: string;
      code: string;
      complement: string;
      isRequired: boolean;
      triviaID: number;
      displayOrder: number;
    }) {
      self.isAddQuestionLoading = true;

      try {
        const { id } = yield api.put(
          /* `/trivia/${payload.id}/add-question`, */
          `${pathTrivia}/question/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isAddQuestionLoading = false;
      }
    }),

    addAnswerOption: flow(function*(payload: any) {
      self.isTriviaManaging = true;
      try {
        const { id } = yield api.post(
          `${pathTrivia}/answer-option/`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTriviaManaging = false;
      }
    }),
  }));

export default TriviaStore.create();
